<template>
  <div class="data-grid">
    <!-- Search and filter bar. Hidden entirely when the screen supplies its own. -->
    <div v-if="showSearch || showRefresh || $slots.filters || $slots.filterActions" class="dg-toolbar">
      <div class="col-md-12">
        <div class="d-flex justify-content-between align-items-center gap-2">
          <!-- Left side: Custom filters slot -->
          <div class="d-flex gap-2 flex-grow-0">
            <slot name="filters"></slot>
          </div>
          
          <!-- Right side: Refresh and Search -->
          <div class="d-flex gap-2">
            <!-- Custom filter actions slot (e.g. collapse toggle) -->
            <slot name="filterActions"></slot>
            <button
              v-if="showRefresh"
              class="btn btn-outline-secondary btn-sm"
              type="button"
              @click="$emit('refresh')"
              :disabled="loading"
              :title="$t('common.refresh')"
              style="border-color: #ced4da; border-width: 1px;"
            >
              <i class="ph-duotone ph-arrow-clockwise" :class="{ 'spin': loading }" style="font-size: 0.875rem;"></i>
            </button>
            <div v-if="showSearch" class="input-group input-group-sm" style="max-width: 250px;">
              <span class="input-group-text"><i class="ti ti-search"></i></span>
              <input 
                type="text" 
                class="form-control"
                :placeholder="searchPlaceholder || $t('common.search.inRequests')" 
                v-model="localSearchText"
                @keyup.enter="handleSearch"
                @blur="handleSearch"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
      <button type="button" class="btn-close float-end" :aria-label="$t('common.close')" @click="$emit('update:error', null)"></button>
    </div>
    
    <!-- Loading indicator -->
    <div v-if="isBusy" class="text-center my-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('common.loading') }}</span>
      </div>
    </div>
    
    <!-- Empty state message -->
    <div v-if="!isBusy && !error && filteredData.length === 0" class="alert alert-info">
      {{ emptyStateMessage || $t('common.noResults') }}
    </div>

    <!-- Table -->
    <div v-if="!isBusy && !error && filteredData.length > 0" class="table-responsive">
      <table class="table table-hover resizable-table">
        <caption class="visually-hidden">
          {{ tableCaption || $t('common.dataTableCaption') }}
        </caption>
        <thead>
          <tr>
            <th v-if="selectable" class="dg-check-col">
              <input
                type="checkbox"
                class="form-check-input"
                :checked="allOnPageSelected"
                :indeterminate="someOnPageSelected"
                :aria-label="$t('common.selectAll')"
                @change="toggleAllOnPage"
              />
            </th>
            <th 
              v-for="(column, index) in columns" 
              :key="column.key" 
              :style="{ width: columnWidths[index] }" 
              class="position-relative"
              :class="{ 'sortable': column.sortable, 'sort-active': column.sortable && sortField === column.sortKey }"
              @click="column.sortable && handleSort(column.sortKey || column.key)"
            >
              <div class="d-flex align-items-center justify-content-between">
                <span>{{ column.label }}</span>
                <span v-if="column.sortable" class="sort-icons ms-2">
                  <i 
                    class="ph-duotone ph-caret-up sort-icon sort-up" 
                    :class="{ 'sort-active': sortField === (column.sortKey || column.key) && sortDirection === 'asc' }"
                  ></i>
                  <i 
                    class="ph-duotone ph-caret-down sort-icon sort-down" 
                    :class="{ 'sort-active': sortField === (column.sortKey || column.key) && sortDirection === 'desc' }"
                  ></i>
                </span>
              </div>
              <div 
                class="column-resizer" 
                @mousedown.prevent="startResize($event, index)"
              ></div>
            </th>
            <th v-if="hasActions" :style="{ width: columnWidths[columns.length] }" class="text-center position-relative">
              {{ $t('common.actionsLabel') }}
              <div 
                class="column-resizer" 
                @mousedown.prevent="startResize($event, columns.length)"
              ></div>
            </th>
          </tr>
        </thead>
        <tbody>
          <!--
            _rowAccent paints the 3px left edge some screens use to carry a row's state at
            a glance. It is per row and computed by the caller, so it arrives on the item
            rather than through a prop.
          -->
          <tr
            v-for="item in paginatedData"
            :key="getItemKey(item)"
            :class="[item._rowClass, { 'has-accent': item._rowAccent }]"
            :style="item._rowAccent ? { borderLeftColor: item._rowAccent } : null"
            style="cursor: pointer;"
            @dblclick="handleRowDoubleClick(item)"
          >
            <td v-if="selectable" class="dg-check-col">
              <input
                type="checkbox"
                class="form-check-input"
                :checked="!!selected[getItemKey(item)]"
                :aria-label="$t('common.select')"
                @change="toggleRow(item)"
                @dblclick.stop
              />
            </td>
            <td v-for="column in columns" :key="column.key">
              <!--
                Per-column slot. A screen that needs a cell the built-in types cannot
                express renders it here instead of forking the whole grid; everything
                else falls through to the default below.
              -->
              <slot :name="`cell-${column.key}`" :item="item" :column="column">
              <div v-if="column.type === 'badge'" class="cell-content" :class="{ 'badge-with-subtitle': item[column.key + 'Subtitle'] }">
                <span 
                  :class="getBadgeClass(item[column.key], column.badgeMap)"
                  :style="item[column.key + 'Color'] ? { backgroundColor: item[column.key + 'Color'], color: getContrastTextColor(item[column.key + 'Color']) } : null"
                >{{ item[column.key + 'Label'] ?? item[column.key] }}</span>
                <small v-if="item[column.key + 'Subtitle']" class="text-muted text-truncate d-block mt-1" :title="item[column.key + 'Subtitle']">{{ item[column.key + 'Subtitle'] }}</small>
              </div>
              <div v-else-if="column.type === 'icon'" class="cell-content">
                <i v-if="item[column.key]" :class="column.iconClass"></i>
              </div>
              <div v-else-if="column.type === 'truncate'" class="cell-content text-truncate" :title="item[column.key]">
                <div v-if="item[column.key + 'Subtitle']">
                  <div class="fw-bold text-truncate">{{ item[column.key] }}</div>
                  <small class="text-muted text-truncate d-block">{{ item[column.key + 'Subtitle'] }}</small>
                </div>
                <div v-else class="text-truncate">{{ item[column.key] }}</div>
              </div>
              <div v-else-if="column.key === 'buyer'" class="cell-content fw-bold text-truncate" :title="item[column.key]">
                {{ item[column.key] }}
              </div>
              <div v-else-if="item[column.key + 'Subtitle']" class="cell-content">
                <div class="text-truncate" :title="item[column.key]">{{ item[column.key] }}</div>
                <small class="text-muted text-truncate d-block" :title="item[column.key + 'Subtitle']">{{ item[column.key + 'Subtitle'] }}</small>
              </div>
              <div v-else class="cell-content text-truncate" :title="item[column.key]">
                {{ item[column.key] }}
              </div>
              </slot>
            </td>
            <td v-if="hasActions" class="text-center">
              <!-- Check if custom actions slot is provided -->
              <slot name="actions" :item="item">
                <!-- Default actions rendering -->
                <ul class="list-inline mb-0">
                  <li v-if="actions.view || actions.edit" class="list-inline-item">
                    <a href="#" @click.prevent="$emit('view', item)" v-if="item.status === 'Quotation Submitted' || item.status === 'Declined' || item.status === 'Expired' || item.trackingStatus === 7" class="btn btn-icon btn-sm btn-outline-primary">
                      <i class="ph-duotone ph-eye"></i>
                    </a>
                    <a href="#" @click.prevent="$emit('edit', item)" v-else class="btn btn-icon btn-sm btn-outline-success">
                      <i class="ph-duotone ph-pencil-simple"></i>
                    </a>
                  </li>
                  <li v-for="(action, index) in getVisibleActions(item)" :key="index" class="list-inline-item">
                    <a 
                      href="#" 
                      @click.prevent="handleActionClick(action, item)" 
                      :class="[
                        action.buttonClass || 'btn btn-icon btn-sm btn-outline-secondary',
                        { 'disabled': action.loading?.(item) }
                      ]"
                      :title="action.title || action.event"
                    >
                      <span v-if="action.loading?.(item)" class="spinner-border spinner-border-sm"></span>
                      <i v-else :class="action.icon"></i>
                    </a>
                  </li>
                </ul>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- Pagination -->
      <div v-if="paginatedData.length > 0" class="dg-foot d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <!--
            One parameterised string, not five fragments. common.pagination.showing already
            reads "Showing {from} to {to} of {total}", so calling it without arguments
            dropped the numbers and left "Showing to of" in front of a second, hand-built
            count.
          -->
          <span class="me-3">
            {{ $t('common.pagination.showing', {
              from: Math.min((localCurrentPage - 1) * localPageSize + 1, getTotalItemsCount()),
              to: Math.min(localCurrentPage * localPageSize, getTotalItemsCount()),
              total: getTotalItemsCount()
            }) }}
          </span>
          <div class="d-flex align-items-center">
            <span class="me-2">{{ $t('common.pagination.show') }}</span>
            <select class="form-select form-select-sm" v-model="localPageSize" @change="onPageSizeChange">
              <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
            </select>
          </div>
        </div>
        <nav :aria-label="$t('common.pagination.navigation')">
          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: localCurrentPage === 1 }">
              <a class="page-link" @click.prevent="changePage(localCurrentPage - 1)" :aria-label="$t('common.pagination.previous')">
                {{ $t('common.pagination.prev') }}
              </a>
            </li>
            
            <!-- First Page -->
            <li v-if="totalPages > 0" class="page-item" :class="{ active: localCurrentPage === 1 }">
              <a class="page-link" @click.prevent="changePage(1)">1</a>
            </li>
            
            <!-- Ellipsis for pages before current group -->
            <li v-if="localCurrentPage > 3" class="page-item disabled">
              <span class="page-link">...</span>
            </li>
            
            <!-- Pages around current page (2 before and 2 after) -->
            <template v-for="page in getVisiblePages()" :key="page">
              <li v-if="page > 1 && page < totalPages" class="page-item" :class="{ active: page === localCurrentPage }">
                <a class="page-link" @click.prevent="changePage(page)">{{ page }}</a>
              </li>
            </template>
            
            <!-- Ellipsis for pages after current group -->
            <li v-if="localCurrentPage < totalPages - 2" class="page-item disabled">
              <span class="page-link">...</span>
            </li>
            
            <!-- Last Page -->
            <li v-if="totalPages > 1" class="page-item" :class="{ active: localCurrentPage === totalPages }">
              <a class="page-link" @click.prevent="changePage(totalPages)">{{ totalPages }}</a>
            </li>
            
            <li class="page-item" :class="{ disabled: localCurrentPage >= totalPages }">
              <a class="page-link" @click.prevent="changePage(localCurrentPage + 1)" :aria-label="$t('common.pagination.next')">
                {{ $t('common.pagination.next') }}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useColumnResize } from '@/composables/useColumnResize';

export default {
  name: 'DataGrid',
  props: {
    // Data props
    data: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    itemKey: {
      type: String,
      default: 'id'
    },
    
    // Pagination props
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    },
    pageSizeOptions: {
      type: Array,
      default: () => [5, 10, 25, 50, 100]
    },
    totalItems: {
      type: Number,
      default: 0
    },
    
    // Search props
    searchText: {
      type: String,
      default: ''
    },
    searchPlaceholder: {
      type: String,
      default: ''
    },
    
    // Action props
    actions: {
      type: Object,
      default: () => ({
        view: true,
        edit: true
      })
    },
    customActions: {
      type: Array,
      default: () => []
    },
    
    // State props
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    
    // UI props
    emptyStateMessage: {
      type: String,
      default: ''
    },
    tableCaption: {
      type: String,
      default: ''
    },
    showRefresh: {
      type: Boolean,
      default: false
    },
    
    // Column resizing
    storageKey: {
      type: String,
      default: 'dataGrid_columnWidths'
    },
    
    // Sorting props
    sortField: {
      type: String,
      default: ''
    },
    sortDirection: {
      type: String,
      default: 'asc', // 'asc' or 'desc'
      validator: (value) => ['asc', 'desc'].includes(value)
    },

    /** Off for screens that carry their own search in a filter bar of their own. */
    showSearch: {
      type: Boolean,
      default: true
    },

    // Selection props
    /** Adds the checkbox column. Off by default: most grids have nothing to bulk-act on. */
    selectable: {
      type: Boolean,
      default: false
    },
    /**
     * Selected rows as { [itemKey]: true }. A map rather than an array so a screen can
     * test one row in constant time while rendering, and so selection survives paging.
     */
    selected: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'update:currentPage', 
    'update:pageSize', 
    'update:searchText',
    'update:error',
    'update:sortField',
    'update:sortDirection',
    'update:selected',
    'search', 
    'view', 
    'edit',
    'pageSizeChange',
    'pageChange',
    'sort'
  ],
  setup(props, { emit }) {
    const localCurrentPage = ref(props.currentPage);
    const localPageSize = ref(props.pageSize);
    const localSearchText = ref(props.searchText);
    // True while a search has been typed but the request has not started/finished yet
    const searchPending = ref(false);
    const sortField = ref(props.sortField);
    const sortDirection = ref(props.sortDirection);
    
    // Check if actions column is needed
    const hasActions = computed(() => {
      return props.actions.view || props.actions.edit || (props.customActions && props.customActions.length > 0);
    });
    
    // Create columns array including actions column if needed
    const columnsWithActions = computed(() => {
      const cols = [...props.columns];
      if (hasActions.value) {
        cols.push({ width: '150px', key: 'actions', label: 'Actions' });
      }
      return cols;
    });
    
    // Initialize column resizing with composable
    const { columnWidths, startResize } = useColumnResize(columnsWithActions.value, props.storageKey);
    
    // Watch for prop changes
    watch(() => props.currentPage, (newVal) => {
      localCurrentPage.value = newVal;
    });
    
    watch(() => props.pageSize, (newVal) => {
      if (newVal !== localPageSize.value) {
        localPageSize.value = newVal;
      }
    });
    
    watch(() => props.searchText, (newVal) => {
      localSearchText.value = newVal;
    });
    
    watch(() => props.sortField, (newVal) => {
      sortField.value = newVal;
    });
    
    watch(() => props.sortDirection, (newVal) => {
      sortDirection.value = newVal;
    });
    
    // Watch for changes in the search text
    let searchDebounceTimer = null;
    watch(localSearchText, (newVal) => {
      emit('update:searchText', newVal);

      // Mark a search as pending so the empty state is not shown before the response arrives
      searchPending.value = true;

      if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        handleSearch();
      }, 400);
    });

    // The parent turns "loading" on when it starts the request; from that moment on
    // the parent's loading flag is the single source of truth.
    watch(() => props.loading, (isLoading) => {
      if (isLoading) {
        searchPending.value = false;
      }
    });

    onBeforeUnmount(() => {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    });
    
    // Computed properties
    // Show the spinner (and hide the empty state) while loading or while a search is queued
    const isBusy = computed(() => props.loading || searchPending.value);

    const filteredData = computed(() => {
      // Client-side filtering only. When the parent passes totalItems the list is paged and
      // searched on the server, so the rows are already the search result: filtering them again
      // here would only match column keys and drop hits found in fields no column is keyed on
      // (e.g. the order number, which is drawn by a slot under the "main" key).
      if (props.totalItems > 0 || !localSearchText.value) return props.data;
      
      const searchTerm = localSearchText.value.toLowerCase();
      return props.data.filter(item => {
        return props.columns.some(column => {
          if (!item[column.key]) return false;
          return String(item[column.key]).toLowerCase().includes(searchTerm);
        });
      });
    });
    
    const totalItems = computed(() => filteredData.value.length);
    
    const totalPages = computed(() => {
      // API'den gelen toplam öğe sayısı varsa onu kullan
      if (props.totalItems > 0) {
        return Math.max(1, Math.ceil(props.totalItems / localPageSize.value));
      }
      
      // API'den toplam sayı gelmemişse client-side hesaplama yap
      return Math.max(1, Math.ceil(totalItems.value / localPageSize.value));
    });
    
    const paginatedData = computed(() => {
      // API-based pagination: data doğrudan API'den gelen tek sayfalık veriyi içerir
      // Bu durumda doğrudan data'yı göster
      if (props.totalItems > 0) {
        return props.data;
      }
      
      // Client-side pagination (API'dan gelmiyorsa)
      const start = (localCurrentPage.value - 1) * localPageSize.value;
      const end = start + localPageSize.value;
      return filteredData.value.slice(start, end);
    });
    
    // Methods
    const handleSearch = () => {
      localCurrentPage.value = 1;
      emit('update:currentPage', 1);
      emit('update:searchText', localSearchText.value);
      emit('search', localSearchText.value);

      // Safety net: if the parent filters client-side and never sets "loading",
      // release the pending flag once the emitted search has been handled.
      nextTick(() => {
        if (!props.loading) {
          searchPending.value = false;
        }
      });
    };
    
    const onPageSizeChange = () => {
      // Update the page size model and emit the update event
      emit('update:pageSize', localPageSize.value);
      emit('pageSizeChange');
    };
    
    const getVisiblePages = () => {
      if (totalPages.value <= 1) {
        return [];
      }
      
      // If total pages is 5 or less, show all pages
      if (totalPages.value <= 5) {
        const pages = Array.from({ length: totalPages.value - 1 }, (_, i) => i + 2);
        return pages;
      }
      
      // Show 2 pages before and 2 pages after current page
      let start = Math.max(2, localCurrentPage.value - 2);
      let end = Math.min(totalPages.value - 1, localCurrentPage.value + 2);
      
      // Adjust range to always show 5 pages when possible
      if (end - start + 1 < 5) {
        if (start === 2) {
          end = Math.min(totalPages.value - 1, start + 4);
        } else if (end === totalPages.value - 1) {
          start = Math.max(2, end - 4);
        }
      }
      
      return Array.from({ length: end - start + 1 }, (_, i) => i + start);
    };
    
    const changePage = (page) => {
      if (page < 1 || page > totalPages.value) return;
      
      localCurrentPage.value = page;
      emit('update:currentPage', page);
      emit('pageChange', page);
    };
    
    const getItemKey = (item) => {
      return item[props.itemKey];
    };

    // ── selection ──────────────────────────────────────────────────────────
    // Select-all acts on the page, not the whole result set: the grid only holds the rows
    // it was given, and a checkbox that silently selected records off-screen would make
    // the bulk actions above it mean something different from what is visible.
    const pageKeys = computed(() => paginatedData.value.map(getItemKey));

    const allOnPageSelected = computed(() =>
      pageKeys.value.length > 0 && pageKeys.value.every((key) => props.selected[key]));

    const someOnPageSelected = computed(() =>
      !allOnPageSelected.value && pageKeys.value.some((key) => props.selected[key]));

    const toggleRow = (item) => {
      const key = getItemKey(item);
      const next = { ...props.selected };

      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }

      emit('update:selected', next);
    };

    const toggleAllOnPage = () => {
      const next = { ...props.selected };

      if (allOnPageSelected.value) {
        pageKeys.value.forEach((key) => delete next[key]);
      } else {
        pageKeys.value.forEach((key) => { next[key] = true; });
      }

      emit('update:selected', next);
    };

    
    const getBadgeClass = (value, badgeMap) => {
      if (badgeMap?.[value]) {
        return `badge ${badgeMap[value]}`;
      }
      
      const normalizedValue = String(value ?? '').toLowerCase();
      if (!normalizedValue) {
        return 'badge bg-light-secondary';
      }
      
      if (normalizedValue.includes('success') || normalizedValue.includes('submitted') || normalizedValue.includes('approved')) {
        return 'badge bg-light-success';
      } else if (normalizedValue.includes('warning') || normalizedValue.includes('waiting') || normalizedValue.includes('pending')) {
        return 'badge bg-light-warning';
      } else if (normalizedValue.includes('danger') || normalizedValue.includes('error') || normalizedValue.includes('failed') || normalizedValue.includes('revision')) {
        return 'badge bg-light-danger';
      }
      
      return 'badge bg-light-secondary';
    };

    const getContrastTextColor = (bg) => {
      if (!bg) return '#212529'
      let hex = bg.trim()
      if (hex.startsWith('rgb')) {
        try {
          const m = hex.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
          if (m) {
            const r = parseInt(m[1], 10), g = parseInt(m[2], 10), b = parseInt(m[3], 10)
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b)
            return luminance > 186 ? '#212529' : '#fff'
          }
        } catch {}
      }
      if (hex[0] === '#') hex = hex.slice(1)
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('')
      }
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b)
      return luminance > 186 ? '#212529' : '#fff'
    };
    
    const handleRowDoubleClick = (item) => {
      // Double-click should trigger edit action, but only if edit is available
      // Check if the item can be edited (not submitted or declined)
      if (item.status === 'Quotation Submitted' || item.status === 'Declined') {
        // If it's submitted or declined, trigger view instead
        emit('view', item);
      } else {
        // Otherwise, trigger edit
        emit('edit', item);
      }
    };
    
    const getTotalItemsCount = () => {
      // API'den gelen toplam öğe sayısı varsa onu kullan
      if (props.totalItems > 0) {
        return props.totalItems;
      }
      // API'den toplam sayı gelmemişse client-side hesaplama yap
      return totalItems.value;
    };
    
    const handleSort = (field) => {
      // If clicking on the same field, toggle direction
      if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        // If clicking on a new field, set to ascending
        sortField.value = field;
        sortDirection.value = 'asc';
      }
      
      // Reset to first page when sorting
      localCurrentPage.value = 1;
      emit('update:currentPage', 1);
      emit('update:sortField', sortField.value);
      emit('update:sortDirection', sortDirection.value);
      emit('sort', { field: sortField.value, direction: sortDirection.value });
    };
    
    const getVisibleActions = (item) => {
      if (!props.customActions || !Array.isArray(props.customActions)) {
        return [];
      }
      
      return props.customActions.filter(action => {
        // If action has a condition function, check it
        if (typeof action.condition === 'function') {
          const result = action.condition(item);
          return result;
        }
        // If no condition, always show
        return true;
      });
    };
    
    const handleActionClick = (action, item) => {
      // Don't trigger if loading
      if (action.loading?.(item)) {
        return;
      }
      emit(action.event, item);
    };
    
    return {
      localCurrentPage,
      localPageSize,
      localSearchText,
      isBusy,
      hasActions,
      filteredData,
      paginatedData,
      totalPages,
      handleSearch,
      onPageSizeChange,
      changePage,
      getItemKey,
      allOnPageSelected,
      someOnPageSelected,
      toggleRow,
      toggleAllOnPage,
      getBadgeClass,
      getContrastTextColor,
      handleRowDoubleClick,
      getVisiblePages,
      getTotalItemsCount,
      handleSort,
      sortField,
      sortDirection,
      columnWidths,
      startResize,
      getVisibleActions,
      handleActionClick
    };
  }
};
</script>

<style scoped>
/* ============================================================
   VESPROC design-system styling for the grid.

   Mirrors the row list in the design project's supplier screens: an uppercase header rule,
   quiet row separators, a soft hover, and an optional coloured left edge carrying a row's
   state. It lives here rather than on one screen so every grid in the app reads the same —
   the alternative was a bespoke layout per screen, which is how the two drift apart.
   ============================================================ */

.data-grid .table > :not(caption) > * > * {
  padding: 13px 14px;
}

.data-grid .table thead th {
  background: var(--vp-surface);
  border-bottom: 1px solid var(--vp-gray-200);
  font-size: 0.7rem;
  letter-spacing: 0.07em;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--vp-gray-500);
  white-space: nowrap;
}

.data-grid .table thead th.sortable:hover,
.data-grid .table thead th.sort-active {
  color: var(--vp-primary);
}

.data-grid .table tbody td {
  border-bottom: 1px solid #f4f7fa;
  font-size: 0.84rem;
  color: var(--vp-body-color);
}

.data-grid .table.table-hover > tbody > tr:hover > * {
  background-color: #fbfdff;
}

/*
 * The toolbar and the pager are tinted strips that run the full width of the card, with
 * the rows between them — the same shape the design draws. They carry their own padding
 * because the card around them has none.
 */
.data-grid .dg-toolbar,
.data-grid .dg-foot {
  padding: 13px 18px;
  background: #fbfcfe;
  margin: 0;
}

.data-grid .dg-toolbar {
  border-bottom: 1px solid var(--vp-gray-200);
}

.data-grid .dg-foot {
  font-size: 0.78rem;
  color: var(--vp-gray-500);
}

/* Square page buttons with a solid current page, rather than Bootstrap's link strip. */
.data-grid .dg-foot .pagination {
  gap: 6px;
}

.data-grid .dg-foot .page-link {
  min-width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--vp-gray-300);
  background: var(--vp-surface);
  color: var(--vp-gray-600);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  padding: 0 8px;
}

.data-grid .dg-foot .page-link:hover {
  background: var(--vp-gray-200);
}

.data-grid .dg-foot .page-item.active .page-link {
  background: var(--vp-gray-800);
  border-color: var(--vp-gray-800);
  color: #fff;
  font-weight: 700;
}

.data-grid .dg-foot .page-item.disabled .page-link {
  color: var(--vp-gray-400);
  background: var(--vp-surface);
}

.data-grid .dg-check-col {
  width: 40px;
  text-align: center;
}

/* The edge is always present so a row with an accent does not shift sideways by 3px. */
.data-grid .table tbody tr {
  border-left: 3px solid transparent;
}

.data-grid .table tbody tr.has-accent {
  border-left-style: solid;
}

/* Ensure tables maintain their cell widths */
.table-responsive {
  overflow-x: auto;
  overflow-y: visible;
}

.table {
  table-layout: fixed;
  width: 100%;
}

.table th {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.table td {
  overflow: hidden;
  vertical-align: middle;
}

/* Allow actions column to show dropdowns */
.table tbody {
  overflow: visible;
}

.table tr {
  overflow: visible;
}

.table td:last-child {
  overflow: visible;
  position: relative;
}

.cell-content {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

/* Allow badge content to display normally without truncation */
.cell-content .badge {
  white-space: normal;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 105px;
  min-height: 30px;
}

/* Badge with subtitle should be block to allow subtitle below */
.cell-content.badge-with-subtitle {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.cell-content.badge-with-subtitle .badge {
  display: block;
  width: fit-content;
}

/* Ensure truncate type columns work properly */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resizable-table thead {
  overflow: visible;
}

.resizable-table th {
  position: relative;
  overflow: hidden;
}

.column-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  z-index: 1;
}

.column-resizer:hover,
.column-resizer:active {
  background-color: #0d6efd;
}

/* Add global style for cursor when resizing */
:global(.resizing-columns) {
  cursor: col-resize !important;
  user-select: none !important;
}

/* Add a subtle indicator for resizable columns */
.resizable-table th:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

/* Make the resize handle more visible on hover */
.resizable-table th:hover .column-resizer {
  background-color: rgba(13, 110, 253, 0.3);
}

/* Sorting styles */
.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.sort-icons {
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0.3;
  transition: opacity 0.2s;
}

.sortable:hover .sort-icons {
  opacity: 0.6;
}

.sort-icon {
  font-size: 0.75rem;
  line-height: 1;
}

.sort-icon.sort-active {
  opacity: 1;
  color: #0d6efd;
}

.sort-active .sort-icons {
  opacity: 1;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style> 